// Redaktion: Beiträge schreiben, zur Freigabe geben, planen und veröffentlichen.
//
// Der Editor benutzt dieselben Bausteine wie der Seiten-Baukasten – nur die,
// die in einen Beitrag gehören. Veröffentlicht wird über den Freigabe-Ablauf:
// Moderation schreibt, die Redaktionsleitung gibt frei. Ist die Freigabe für
// dieses Radio ausgeschaltet, veröffentlicht jede Moderation selbst.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BLOCKS, blockId, type BlockKind, type PageBlock } from "@webradiosuite/shared/pages";
import {
  POST_BLOCK_KINDS,
  POST_STATUS_LABEL,
  nextPostStatus,
  type PostAction,
  type PostStatus,
} from "@webradiosuite/shared/posts";
import {
  postsApi,
  type PostDetail,
  type PostFilter,
  type PostsOverview,
  type PostSummary,
} from "../api";
import { useAuth } from "../auth/AuthProvider";
import { BlockForm, ImageField } from "../components/block-fields";
import { EmptyState, Page, PageHeader, Skeleton, Tabs } from "../components/ui";

const inputClass = "input mt-1.5 font-normal";

// Status als Etikett: Entwurf grau, Freigabe gelb, geplant blau, veröffentlicht grün.
const STATUS_CLASS: Record<PostStatus, string> = {
  draft: "badge-neutral",
  review: "badge-warning",
  scheduled: "badge-info",
  published: "badge-success",
  retracted: "badge-danger",
};

function StatusTag({ status }: { status: PostStatus }): JSX.Element {
  return (
    <span className={`badge whitespace-nowrap ${STATUS_CLASS[status]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {POST_STATUS_LABEL[status]}
    </span>
  );
}

function when(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message.replace(/^\d+\s*/, "") : fallback;
}

type Tab = "posts" | "categories" | "settings";

export function RedaktionPage(): JSX.Element {
  const { user } = useAuth();
  const [overview, setOverview] = useState<PostsOverview | null>(null);
  const [filter, setFilter] = useState<PostFilter>({});
  const [tab, setTab] = useState<Tab>("posts");
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setOverview(await postsApi.overview(filter));
      setError(null);
    } catch (err) {
      setError(errorText(err, "Die Beiträge ließen sich nicht laden."));
    }
  }, [filter]);

  useEffect(() => { void reload(); }, [reload]);

  if (!overview) {
    return (
      <Page className="max-w-6xl">
        {error ? <div className="callout callout-danger">{error}</div> : (
          <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-4 w-96 max-w-full" /><Skeleton className="h-64" /></div>
        )}
      </Page>
    );
  }

  if (editing) {
    return (
      <PostEditor
        postId={editing}
        overview={overview}
        onClose={() => { setEditing(null); void reload(); }}
      />
    );
  }

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Programm"
        title="Redaktion"
        description={<>
          Hier schreibt ihr Beiträge für eure Hörerseite. Ein Beitrag wird aus denselben Bausteinen gebaut wie eine Seite.
          {overview.review_required
            ? " Bevor ein Beitrag erscheint, gibt die Redaktionsleitung ihn frei."
            : " Die Freigabe ist für dieses Radio ausgeschaltet – wer schreibt, veröffentlicht auch."}
        </>}
      >
        <div className="-mx-1 overflow-x-auto px-1">
          <Tabs
            value={tab}
            onChange={setTab}
            className="whitespace-nowrap"
            items={([["posts", `Beiträge${overview.waiting > 0 ? ` (${overview.waiting} warten)` : ""}`], ["categories", "Kategorien"], ["settings", "Einstellungen"]] as Array<[Tab, string]>).map(([id, label]) => ({ value: id, label }))}
          />
        </div>
      </PageHeader>

      <div className="space-y-4">
      {error && <div className="callout callout-danger">{error}</div>}

      {tab === "posts" && (
        <PostsTab overview={overview} filter={filter} onFilter={setFilter} onEdit={setEditing} onChanged={reload}
          isTrainee={user?.role === "trainee"} />
      )}
      {tab === "categories" && <CategoriesTab overview={overview} onChanged={reload} />}
      {tab === "settings" && <SettingsTab overview={overview} onChanged={reload} />}
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------- Liste

function PostsTab({ overview, filter, onFilter, onEdit, onChanged, isTrainee }: {
  overview: PostsOverview;
  filter: PostFilter;
  onFilter: (f: PostFilter) => void;
  onEdit: (id: string) => void;
  onChanged: () => Promise<void>;
  isTrainee: boolean;
}): JSX.Element {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (): Promise<void> => {
    if (title.trim() === "") return;
    setBusy(true);
    try {
      const post = await postsApi.create({ title: title.trim() });
      setTitle("");
      await onChanged();
      onEdit(post.id);
    } catch (err) {
      setError(errorText(err, "Der Beitrag ließ sich nicht anlegen."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="card grid grid-cols-2 items-end gap-3 p-3 sm:p-4 md:grid-cols-4">
        <label className="block text-[0.8rem] font-medium text-secondary">Status
          <select value={filter.status ?? ""} onChange={(e) => onFilter({ ...filter, status: (e.target.value || undefined) as PostStatus | undefined })}
            className={inputClass}>
            <option value="">alle</option>
            {(Object.keys(POST_STATUS_LABEL) as PostStatus[]).map((s) => <option key={s} value={s}>{POST_STATUS_LABEL[s]}</option>)}
          </select>
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">Kategorie
          <select value={filter.kategorie ?? ""} onChange={(e) => onFilter({ ...filter, kategorie: e.target.value || undefined })} className={inputClass}>
            <option value="">alle</option>
            {overview.categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">Autor
          <select value={filter.autor ?? ""} onChange={(e) => onFilter({ ...filter, autor: e.target.value || undefined })} className={inputClass}>
            <option value="">alle</option>
            {overview.authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">Suche
          <input value={filter.suche ?? ""} onChange={(e) => onFilter({ ...filter, suche: e.target.value || undefined })}
            placeholder="Wort im Titel" className={inputClass} />
        </label>
      </div>

      {error && <div className="callout callout-danger">{error}</div>}

      <div className="card overflow-hidden">
        {overview.posts.length === 0 ? (
          <div className="p-4"><EmptyState title="Hier ist noch nichts." description="Unten einen ersten Beitrag anlegen." /></div>
        ) : (
          <ul className="divide-y divide-line">
            {overview.posts.map((post) => (
              <PostRow key={post.id} post={post} overview={overview} onEdit={onEdit} onChanged={onChanged} />
            ))}
          </ul>
        )}
        {!isTrainee && (
          <div className="flex flex-wrap items-center gap-2 border-t border-line bg-surface-sunken p-3">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Überschrift des neuen Beitrags"
              aria-label="Überschrift des neuen Beitrags" className="input min-w-0 flex-1 basis-56"
              onKeyDown={(e) => { if (e.key === "Enter") void create(); }} />
            <button onClick={() => void create()} disabled={busy || title.trim() === ""}
              className="btn btn-primary">+ Beitrag anlegen</button>
          </div>
        )}
      </div>
    </div>
  );
}

function PostRow({ post, overview, onEdit, onChanged }: {
  post: PostSummary;
  overview: PostsOverview;
  onEdit: (id: string) => void;
  onChanged: () => Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: PostAction, extra: { publish_at?: string } = {}): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await postsApi.act(post.id, action, extra);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Das hat nicht geklappt."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="px-4 py-3 transition-colors hover:bg-surface-hover">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => onEdit(post.id)} className="group min-w-0 flex-1 basis-60 text-left">
          <div className="truncate font-medium text-primary group-hover:text-brand-primary">{post.title}</div>
          <div className="truncate text-xs text-muted">
            /{overview.tenant_slug}/beitrag/{post.slug}
            {post.author ? ` · ${post.author}` : ""}
            {post.status === "scheduled" && post.publish_at ? ` · erscheint ${when(post.publish_at)}` : ""}
            {post.status === "published" && post.published_at ? ` · seit ${when(post.published_at)}` : ""}
          </div>
        </button>
        <StatusTag status={post.status} />
        <WorkflowButtons post={post} overview={overview} busy={busy} onAct={act} />
      </div>
      {post.categories.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {post.categories.map((c) => <span key={c.id} className="badge badge-neutral">{c.name}</span>)}
        </div>
      )}
      {error && <div className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</div>}
    </li>
  );
}

/** Welche Knöpfe stehen bei diesem Zustand zur Verfügung? Die Regeln kommen aus dem Paket. */
function WorkflowButtons({ post, overview, busy, onAct }: {
  post: PostSummary;
  overview: PostsOverview;
  busy: boolean;
  onAct: (action: PostAction, extra?: { publish_at?: string }) => Promise<void>;
}): JSX.Element {
  const rights = { mayPublish: overview.may_publish, reviewRequired: overview.review_required };
  const can = (action: PostAction): boolean => nextPostStatus(post.status, action, rights).ok;
  const plan = async (): Promise<void> => {
    const answer = window.prompt("Wann soll der Beitrag erscheinen? (Datum und Uhrzeit, z. B. 24.12.2026 18:00)");
    if (!answer) return;
    const at = parseGermanDateTime(answer);
    if (!at) {
      window.alert("Das Datum habe ich nicht verstanden. Bitte so schreiben: 24.12.2026 18:00");
      return;
    }
    await onAct("schedule", { publish_at: at });
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {can("submit") && (
        <button disabled={busy} onClick={() => void onAct("submit")} className="btn btn-secondary btn-sm">Zur Freigabe</button>
      )}
      {can("approve") && (
        <button disabled={busy} onClick={() => void onAct("approve")} className="btn btn-primary btn-sm">Freigeben</button>
      )}
      {can("reject") && (
        <button disabled={busy} onClick={() => void onAct("reject")} className="btn btn-secondary btn-sm">Zurück an den Autor</button>
      )}
      {post.status !== "published" && can("publish") && (
        <button disabled={busy} onClick={() => void onAct("publish")} className={`btn btn-sm ${can("approve") ? "btn-secondary" : "btn-primary"}`}>Veröffentlichen</button>
      )}
      {post.status !== "scheduled" && can("schedule") && (
        <button disabled={busy} onClick={() => void plan()} className="btn btn-secondary btn-sm">Erscheinen planen</button>
      )}
      {can("retract") && (
        <button disabled={busy} onClick={() => void onAct("retract")} className="btn btn-ghost btn-sm text-red-600 dark:text-red-400">Zurückziehen</button>
      )}
    </div>
  );
}

/** „24.12.2026 18:00“ → ISO-Zeitpunkt. Gibt null zurück, wenn es nicht passt. */
export function parseGermanDateTime(value: string): string | null {
  const m = /^\s*(\d{1,2})\.(\d{1,2})\.(\d{4})[ ,]+(\d{1,2}):(\d{2})\s*$/.exec(value);
  if (!m) return null;
  const [, d, mo, y, h, min] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(min), 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getDate() !== Number(d) || date.getMonth() !== Number(mo) - 1) return null;
  return date.toISOString();
}

// ---------------------------------------------------------------- Editor

function PostEditor({ postId, overview, onClose }: {
  postId: string;
  overview: PostsOverview;
  onClose: () => void;
}): JSX.Element {
  const [post, setPost] = useState<PostDetail | null>(null);
  const [blocks, setBlocks] = useState<PageBlock[]>([]);
  const [meta, setMeta] = useState({
    title: "", slug: "", teaser: "", cover_url: "", cover_alt: "",
    show_id: "", seo_title: "", seo_description: "", categories: [] as string[],
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [adding, setAdding] = useState(false);
  const dragFrom = useRef<number | null>(null);

  const load = useCallback(async () => {
    const detail = await postsApi.post(postId);
    setPost(detail);
    setBlocks(detail.blocks);
    setMeta({
      title: detail.title,
      slug: detail.slug,
      teaser: detail.teaser,
      cover_url: detail.cover_url ?? "",
      cover_alt: detail.cover_alt,
      show_id: detail.show_id ?? "",
      seo_title: detail.seo_title ?? "",
      seo_description: detail.seo_description ?? "",
      categories: detail.categories.map((c) => c.name),
    });
    setDirty(false);
  }, [postId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    void postsApi.previewLink(postId).then((res) => setPreviewUrl(res.url)).catch(() => setPreviewUrl(null));
  }, [postId]);

  const choices = useMemo(() => ({
    category: overview.categories.map((c) => ({ value: c.slug, label: c.name })),
    post: overview.posts.filter((p) => p.id !== postId).map((p) => ({ value: p.slug, label: p.title })),
    episode: overview.episodes.map((e) => ({ value: e.slug, label: e.title })),
  }), [overview.categories, overview.posts, overview.episodes, postId]);

  const save = async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await postsApi.save(postId, {
        title: meta.title,
        slug: meta.slug,
        teaser: meta.teaser,
        cover_url: meta.cover_url === "" ? null : meta.cover_url,
        cover_alt: meta.cover_alt,
        show_id: meta.show_id === "" ? null : meta.show_id,
        seo_title: meta.seo_title.trim() === "" ? null : meta.seo_title.trim(),
        seo_description: meta.seo_description.trim() === "" ? null : meta.seo_description.trim(),
        categories: meta.categories,
        blocks,
      });
      setDirty(false);
      setNonce((n) => n + 1);
      setMessage("Gespeichert.");
      await load();
      return true;
    } catch (err) {
      setError(errorText(err, "Das Speichern hat nicht geklappt."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: PostAction, extra: { publish_at?: string } = {}): Promise<void> => {
    if (dirty && !(await save())) return;
    setBusy(true);
    setError(null);
    try {
      await postsApi.act(postId, action, extra);
      await load();
      setNonce((n) => n + 1);
      setMessage(action === "submit" ? "Der Beitrag liegt jetzt bei der Redaktionsleitung."
        : action === "approve" || action === "publish" ? "Der Beitrag steht auf der Hörerseite."
        : action === "retract" ? "Der Beitrag ist wieder verschwunden."
        : "Erledigt.");
    } catch (err) {
      setError(errorText(err, "Das hat nicht geklappt."));
    } finally {
      setBusy(false);
    }
  };

  const change = (next: PageBlock[]): void => { setBlocks(next); setDirty(true); };

  const addBlock = (kind: BlockKind): void => {
    const block: PageBlock = { id: blockId(), kind, props: { ...BLOCKS[kind].defaults } };
    change([...blocks, block]);
    setSelected(block.id);
    setAdding(false);
  };

  const moveBlock = (from: number, to: number): void => {
    if (to < 0 || to >= blocks.length || from === to) return;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    change(next);
  };

  const current = blocks.find((b) => b.id === selected) ?? null;
  const previewSrc = previewUrl ? `${previewUrl}${previewUrl.includes("?") ? "&" : "?"}t=${nonce}` : null;

  if (!post) return <Page><div className="space-y-3"><Skeleton className="h-12" /><Skeleton className="h-96" /></div></Page>;

  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-line bg-card px-4 py-3">
        <button onClick={() => {
          if (dirty && !window.confirm("Es gibt ungespeicherte Änderungen. Trotzdem zurück?")) return;
          onClose();
        }} className="btn btn-ghost">← Beiträge</button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-primary">{meta.title}</div>
          <div className="truncate text-xs text-muted">
            /{overview.tenant_slug}/beitrag/{meta.slug}{dirty ? <span className="text-amber-600 dark:text-amber-400"> · nicht gespeichert</span> : ""}
          </div>
        </div>
        <StatusTag status={post.status} />
        <button onClick={() => void save()} disabled={busy || !dirty}
          className={`btn ${dirty ? "btn-primary" : "btn-secondary"}`}>Speichern</button>
        <WorkflowButtons post={post} overview={overview} busy={busy} onAct={act} />
      </div>

      {(message || error) && (
        <div className={`callout mx-4 mt-3 ${error ? "callout-danger" : "callout-success"}`} role="status">
          {error ?? message}
        </div>
      )}

      {post.source_note && (
        <div className="callout callout-warning mx-4 mt-3 block">
          Dieser Entwurf kam aus einer Meldung ({post.source_note}). Bitte in eigenen Worten schreiben –
          der Wortlaut fremder Meldungen ist urheberrechtlich geschützt. Fakten sind frei.
        </div>
      )}

      <div className="grid flex-1 gap-4 p-4 lg:grid-cols-[260px_1fr_320px]">
        {/* Bausteine */}
        <div className="space-y-2">
          <h2 className="section-label">Bausteine</h2>
          <ul className="space-y-1">
            {blocks.map((block, i) => (
              <li key={block.id}
                draggable
                onDragStart={() => { dragFrom.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (dragFrom.current !== null) moveBlock(dragFrom.current, i); dragFrom.current = null; }}
                className={`rounded-lg border bg-card p-2 shadow-sm transition ${selected === block.id ? "border-brand-primary ring-2 ring-brand-primary/20" : "border-line hover:border-line-strong"}`}>
                <div className="flex items-center gap-1">
                  <button onClick={() => setSelected(block.id)} className="min-w-0 flex-1 text-left text-sm text-primary">
                    <span className="cursor-grab text-muted" aria-hidden="true">⠿ </span>{BLOCKS[block.kind].label}
                  </button>
                  <button onClick={() => moveBlock(i, i - 1)} className="rounded px-1 text-sm text-muted hover:bg-surface-hover hover:text-primary" aria-label={`${BLOCKS[block.kind].label} nach oben`}>↑</button>
                  <button onClick={() => moveBlock(i, i + 1)} className="rounded px-1 text-sm text-muted hover:bg-surface-hover hover:text-primary" aria-label={`${BLOCKS[block.kind].label} nach unten`}>↓</button>
                  <button onClick={() => { change(blocks.filter((b) => b.id !== block.id)); if (selected === block.id) setSelected(null); }}
                    className="rounded px-1 text-sm text-red-600 hover:bg-red-500/10 dark:text-red-400" aria-label={`${BLOCKS[block.kind].label} entfernen`}>✕</button>
                </div>
              </li>
            ))}
          </ul>
          <button onClick={() => setAdding(true)} className="w-full rounded-lg border border-dashed border-line-strong px-2 py-2.5 text-sm font-medium text-secondary transition hover:border-brand-primary hover:bg-brand-primary/5 hover:text-brand-primary">
            + Baustein hinzufügen
          </button>
          {blocks.length === 0 && <p className="text-xs text-muted">Dieser Beitrag hat noch keinen Inhalt.</p>}
        </div>

        {/* Vorschau */}
        <div className="well min-h-[600px] p-2">
          <div className="mb-2 px-1 text-xs text-muted">
            Vorschau – so sieht der Beitrag aus. Der Link daneben gilt zwei Stunden und lässt sich weitergeben.
          </div>
          {previewSrc
            ? <iframe key={nonce} src={previewSrc} title="Vorschau des Beitrags" className="h-[700px] w-full rounded-lg border border-line bg-white shadow-sm" />
            : <div className="p-4 text-sm text-muted">Die Vorschau ist gerade nicht erreichbar.</div>}
        </div>

        {/* Einstellungen */}
        <div className="space-y-4">
          {current ? (
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <h2 className="card-title">{BLOCKS[current.kind].label}</h2>
                <button onClick={() => setSelected(null)} className="btn btn-ghost btn-sm">Schließen</button>
              </div>
              <p className="mb-2 mt-1 text-xs text-muted">{BLOCKS[current.kind].hint}</p>
              <BlockForm
                fields={BLOCKS[current.kind].fields}
                props={current.props}
                channels={[]}
                publicUrl={overview.public_url}
                choices={choices}
                onChange={(props) => change(blocks.map((b) => (b.id === current.id ? { ...b, props } : b)))}
              />
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-line-strong p-4 text-center text-sm text-muted">
              Links einen Baustein anklicken, um ihn einzustellen.
            </p>
          )}

          <details className="card p-4" open={!current}>
            <summary className="card-title cursor-pointer select-none">Beitrag</summary>
            <div className="mt-3 space-y-3">
              <label className="block text-[0.8rem] font-medium text-secondary">Überschrift
                <input value={meta.title} onChange={(e) => { setMeta({ ...meta, title: e.target.value }); setDirty(true); }} className={inputClass} />
              </label>
              <label className="block text-[0.8rem] font-medium text-secondary">Adresse (hinter /beitrag/)
                <input value={meta.slug} onChange={(e) => { setMeta({ ...meta, slug: e.target.value }); setDirty(true); }} className={inputClass} />
              </label>
              <label className="block text-[0.8rem] font-medium text-secondary">Anrisstext (steht in Listen und beim Teilen)
                <textarea rows={3} maxLength={600} value={meta.teaser}
                  onChange={(e) => { setMeta({ ...meta, teaser: e.target.value }); setDirty(true); }} className={inputClass} />
              </label>
              <div className="text-[0.8rem] font-medium text-secondary">Titelbild
                <ImageField value={meta.cover_url} onChange={(v) => { setMeta({ ...meta, cover_url: v }); setDirty(true); }}
                  publicUrl={overview.public_url} label="Titelbild" />
              </div>
              {meta.cover_url && (
                <label className="block text-[0.8rem] font-medium text-secondary">Was ist auf dem Bild zu sehen?
                  <input value={meta.cover_alt} maxLength={200}
                    onChange={(e) => { setMeta({ ...meta, cover_alt: e.target.value }); setDirty(true); }} className={inputClass} />
                </label>
              )}
              <label className="block text-[0.8rem] font-medium text-secondary">Gehört zur Sendung
                <select value={meta.show_id} onChange={(e) => { setMeta({ ...meta, show_id: e.target.value }); setDirty(true); }} className={inputClass}>
                  <option value="">keine</option>
                  {overview.shows.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </label>
              <fieldset className="well space-y-1">
                <legend className="px-1 text-[0.8rem] font-medium text-secondary">Kategorien</legend>
                {overview.categories.length === 0 && <p className="text-xs text-muted">Noch keine Kategorien angelegt.</p>}
                {overview.categories.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-secondary">
                    <input type="checkbox" checked={meta.categories.includes(c.name)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...meta.categories, c.name]
                          : meta.categories.filter((n) => n !== c.name);
                        setMeta({ ...meta, categories: next });
                        setDirty(true);
                      }} />
                    {c.name}
                  </label>
                ))}
              </fieldset>
              <div className="badge badge-neutral">Lesezeit: etwa {post.reading_minutes} Minute{post.reading_minutes === 1 ? "" : "n"} (wird beim Speichern berechnet).</div>
              <label className="block text-[0.8rem] font-medium text-secondary">Titel für Suchmaschinen (leer: Überschrift)
                <input value={meta.seo_title} onChange={(e) => { setMeta({ ...meta, seo_title: e.target.value }); setDirty(true); }} className={inputClass} />
              </label>
              <label className="block text-[0.8rem] font-medium text-secondary">Kurzbeschreibung für Suchmaschinen (leer: Anrisstext)
                <textarea rows={2} maxLength={300} value={meta.seo_description}
                  onChange={(e) => { setMeta({ ...meta, seo_description: e.target.value }); setDirty(true); }} className={inputClass} />
              </label>
            </div>
          </details>

          <details className="card p-4">
            <summary className="card-title cursor-pointer select-none">Vorschau-Link zum Weitergeben</summary>
            <p className="mt-2 text-xs text-muted">Der Link zeigt den Beitrag und gilt zwei Stunden.</p>
            <div className="mt-2 flex gap-2">
              <input readOnly value={previewUrl ?? ""} className="input h-8 text-xs" aria-label="Vorschau-Link" />
              <button onClick={() => { if (previewUrl) void navigator.clipboard?.writeText(previewUrl); }}
                className="btn btn-secondary btn-sm">Kopieren</button>
            </div>
          </details>

          {post.versions.length > 0 && (
            <details className="card p-4">
              <summary className="card-title cursor-pointer select-none">Frühere Fassungen</summary>
              <ul className="mt-2 space-y-1">
                {post.versions.map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="min-w-0 flex-1 truncate text-muted">
                      {when(v.created_at)}{v.author ? ` · ${v.author}` : ""}{v.note ? ` · ${v.note}` : ""}
                    </span>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => {
                        if (!window.confirm("Diese Fassung zurückholen? Der jetzige Stand wird vorher gesichert.")) return;
                        void postsApi.restoreVersion(postId, v.id).then(() => load()).then(() => setNonce((n) => n + 1));
                      }}>Zurückholen</button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-label="Baustein wählen">
          <div className="card max-h-[80vh] w-full max-w-lg animate-fade-in overflow-auto p-5 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-primary">Baustein wählen</h2>
              <button onClick={() => setAdding(false)} className="btn btn-ghost btn-sm">Schließen</button>
            </div>
            <ul className="grid gap-2 sm:grid-cols-2">
              {POST_BLOCK_KINDS.map((kind) => (
                <li key={kind}>
                  <button onClick={() => addBlock(kind)} className="h-full w-full rounded-lg border border-line p-3 text-left transition hover:border-brand-primary hover:bg-brand-primary/5">
                    <div className="text-sm font-medium text-primary">{BLOCKS[kind].label}</div>
                    <div className="text-xs text-muted">{BLOCKS[kind].hint}</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Kategorien

function CategoriesTab({ overview, onChanged }: { overview: PostsOverview; onChanged: () => Promise<void> }): JSX.Element {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="max-w-2xl space-y-3">
      <p className="text-sm text-muted">
        Kategorien ordnen die Beiträge. Jede Kategorie bekommt eine eigene Seite und einen eigenen RSS-Feed.
      </p>
      <ul className="card divide-y divide-line overflow-hidden">
        {overview.categories.length === 0 && <li className="p-4 text-center text-sm text-muted">Noch keine Kategorien.</li>}
        {overview.categories.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-2 px-4 py-3 hover:bg-surface-hover">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-primary">{c.name}</div>
              <div className="truncate text-xs text-muted">/{overview.tenant_slug}/kategorie/{c.slug} · {c.posts ?? 0} Beiträge</div>
            </div>
            <button className="btn btn-secondary btn-sm"
              onClick={() => {
                const next = window.prompt("Neuer Name der Kategorie", c.name);
                if (!next?.trim()) return;
                void postsApi.renameCategory(c.id, next.trim()).then(onChanged).catch((err) => setError(errorText(err, "Das Umbenennen hat nicht geklappt.")));
              }}>Umbenennen</button>
            <button className="btn btn-ghost btn-sm text-red-600 dark:text-red-400"
              onClick={() => {
                if (!window.confirm(`Kategorie „${c.name}“ löschen? Die Beiträge bleiben erhalten.`)) return;
                void postsApi.deleteCategory(c.id).then(onChanged).catch((err) => setError(errorText(err, "Das Löschen hat nicht geklappt.")));
              }}>Löschen</button>
          </li>
        ))}
        <li className="flex flex-wrap items-center gap-2 bg-surface-sunken p-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name der neuen Kategorie"
            aria-label="Name der neuen Kategorie" className="input min-w-0 flex-1 basis-48" />
          <button disabled={name.trim() === ""} className="btn btn-primary"
            onClick={() => {
              void postsApi.createCategory(name.trim()).then(() => { setName(""); return onChanged(); })
                .catch((err) => setError(errorText(err, "Die Kategorie ließ sich nicht anlegen.")));
            }}>Anlegen</button>
        </li>
      </ul>
      {error && <div className="callout callout-danger">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- Einstellungen

function SettingsTab({ overview, onChanged }: { overview: PostsOverview; onChanged: () => Promise<void> }): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="max-w-2xl space-y-3">
      <div className="card p-5">
        <label className="flex items-start gap-2 text-sm text-secondary">
          <input type="checkbox" checked={overview.review_required} disabled={!overview.is_admin}
            onChange={(e) => {
              void postsApi.setReviewRequired(e.target.checked).then(onChanged)
                .catch((err) => setError(errorText(err, "Das ließ sich nicht ändern.")));
            }} />
          <span>
            <strong>Beiträge erst nach Freigabe veröffentlichen</strong>
            <span className="mt-1 block text-xs text-muted">
              Ist der Haken gesetzt, schreibt die Moderation und die Redaktionsleitung gibt frei.
              Ohne Haken veröffentlicht jede Moderation selbst.
            </span>
          </span>
        </label>
        {!overview.is_admin && <p className="mt-2 text-xs text-muted">Ändern darf das die Leitung.</p>}
      </div>
      <div className="card p-5 text-sm text-secondary">
        <strong className="card-title">Wer darf freigeben?</strong>
        <p className="mt-1 text-xs text-muted">
          Admins dürfen es immer. Wer sonst noch freigeben, planen und zurückziehen darf, bekommt hier den Haken.
          {overview.may_publish ? " Du darfst freigeben." : " Du darfst Beiträge schreiben und zur Freigabe geben."}
        </p>
        <ul className="mt-3 divide-y divide-line rounded-lg border border-line">
          {overview.authors.map((a) => {
            const always = a.role === "tenant_admin" || a.role === "super_admin";
            return (
              <li key={a.id}>
                <label className="flex items-center gap-2 px-3 py-2 text-sm">
                  <input type="checkbox" checked={always || a.editorial_lead} disabled={always || !overview.is_admin}
                    onChange={(e) => {
                      void postsApi.setEditorialLead(a.id, e.target.checked).then(onChanged)
                        .catch((err) => setError(errorText(err, "Das Recht ließ sich nicht ändern.")));
                    }} />
                  <span>{a.name}{always ? " (Leitung, immer)" : ""}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="card p-5 text-sm text-secondary">
        <strong className="card-title">Adressen</strong>
        <ul className="mt-2 list-disc space-y-0.5 pl-5 font-mono text-xs text-muted">
          <li>Beitrag: {overview.public_url}/{overview.tenant_slug}/beitrag/…</li>
          <li>Alle Beiträge: {overview.public_url}/{overview.tenant_slug}/beitraege</li>
          <li>RSS-Feed: {overview.public_url}/{overview.tenant_slug}/beitraege.xml</li>
          <li>Sendung: {overview.public_url}/{overview.tenant_slug}/sendung/…</li>
        </ul>
      </div>
      {error && <div className="callout callout-danger">{error}</div>}
    </div>
  );
}
